import { useCallback, useEffect, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

import { ctaTypes } from "Giving/constants";
import qs from "qs";

import externalNavigate from "Utilities/externalNavigate";

import { useCTAContext } from "./CTA";

const useTypeAsString = () => {
  const { type } = useCTAContext();

  if (type === ctaTypes.GIVE_TO_FUND || type === 1)
    return ctaTypes.GIVE_TO_FUND;
  if (type === ctaTypes.MESSAGE || type === 2) return ctaTypes.MESSAGE;
  if (type === ctaTypes.RECURRING_GIFT || type === 4)
    return ctaTypes.RECURRING_GIFT;
  if (
    type === ctaTypes.CAMPAIGN_STATUS ||
    type === 5 ||
    type === ctaTypes.PLEDGE ||
    type === 3
  )
    return ctaTypes.PLEDGE;

  return undefined;
};

const useCampaignLinkType = () => {
  const {
    userPledgeInfo,
    userRecurringGiftInfo,
    campaignInfo: { pledgingOpen = false } = {},
  } = useCTAContext();
  return useMemo(() => {
    if (pledgingOpen && !userRecurringGiftInfo && !userPledgeInfo) {
      return "pledge";
    } else if (!userRecurringGiftInfo) {
      return "give";
    }
    return "";
  }, [pledgingOpen, userPledgeInfo, userRecurringGiftInfo]);
};

const convertRecurringIntervalToTextValue = (interval) => {
  if (typeof interval !== "number") return "";
  switch (interval) {
    case 1:
      return "weekly";
    case 2:
      return "monthly";
    case 3:
      return "quarterly";
    case 4:
      return "annually";
    default:
      return "";
  }
};

const useCtaUrl = () => {
  const {
    targetFundID,
    link,
    suggestedRecurringGiftInterval,
    suggestedAmount,
    userPledgeInfo,
    campaignInfo: {
      campaignID = null,
      fund: { ID: campaignFundID } = { ID: null },
    } = {},
  } = useCTAContext();
  const linkType = useCampaignLinkType();
  const type = useTypeAsString();

  switch (type) {
    case ctaTypes.GIVE_TO_FUND:
      return `/give/${targetFundID}`;
    case ctaTypes.MESSAGE:
      return link;
    case ctaTypes.RECURRING_GIFT: {
      let url = `/give/${targetFundID}`;
      const queryParams = {};
      if (suggestedRecurringGiftInterval) {
        queryParams.recurringSchedule = convertRecurringIntervalToTextValue(
          suggestedRecurringGiftInterval
        );
      }
      if (suggestedAmount) {
        queryParams.amount = suggestedAmount;
      }
      const paramsAsString = qs.stringify(queryParams);
      if (paramsAsString) url += `?${paramsAsString}`;

      return url;
    }
    case ctaTypes.PLEDGE: {
      let url;

      if (linkType === "pledge") {
        // link to the pledge page
        url = `/pledge/${campaignID}`;
      } else {
        // link to the payment page (with the pledgeID query param included)
        url = `/give/${campaignFundID}`;
        const pledgeID = userPledgeInfo?.pledgeID;
        if (pledgeID) url += `?pledgeID=${pledgeID}`;
        else if (suggestedAmount) url += `?amount=${suggestedAmount}`;
      }
      return url;
    }
    default:
      return () => {};
  }
};

const useOnCTAClick = () => {
  const { newTab, isPreview, previewActionRef } = useCTAContext();
  const type = useTypeAsString();
  const url = useCtaUrl();

  const onCTAClick = useCallback(
    (forceAction) => {
      if (isPreview && !(forceAction === true)) return;
      switch (type) {
        case ctaTypes.GIVE_TO_FUND:
          externalNavigate(url, newTab ? "_blank" : "_self");
          break;
        case ctaTypes.MESSAGE:
          externalNavigate(url, newTab ? "_blank" : "_self");
          break;
        case ctaTypes.RECURRING_GIFT:
          externalNavigate(url, newTab ? "_blank" : "_self");
          break;
        case ctaTypes.PLEDGE:
          externalNavigate(url, newTab ? "_blank" : "_self");
          break;
        default:
          return;
      }
    },
    [isPreview, newTab, type, url]
  );

  if (previewActionRef) previewActionRef.current = () => onCTAClick(true);
  return onCTAClick;
};

const useOnRecurringGiftClick = () => {
  const history = useHistory();
  const location = useLocation();
  const { userRecurringGiftInfo } = useCTAContext();
  if (!userRecurringGiftInfo) {
    return () => {};
  }
  const url = `${location.pathname}/recurring/${userRecurringGiftInfo.recurringGiftID}`;

  return () =>
    history.push({
      userRecurringGiftInfo,
      pathname: url,
      search: location.search,
    });
};

export { useOnCTAClick, useTypeAsString, useCtaUrl, useOnRecurringGiftClick };
